/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AssignmentPanel.tsx
 *     Feature         :   192-alert-rules-junction-remodel
 *     Description     :   Assignment management for an alert rule (FR-010): lists the
 *                         rule's capacity/lakehouse targets by name (entity-first reads),
 *                         add/remove assignments, and the scope badge — Global
 *                         (aggregation-path, zero assignments) vs Unassigned (per-target,
 *                         zero assignments — inert, never global-by-deletion) vs scoped.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X, Globe, CircleOff, Target } from 'lucide-react';
import {
  useRuleAssignments,
  useAddRuleAssignment,
  useRemoveRuleAssignment,
} from '../../../../hooks/useAlertRules';
import { getCapacities } from '../../../../services/capacityApi';
import useLakehouses, { LakehouseWithWorkspace } from '../../../../modules/backup/hooks/useLakehouses';

interface AssignmentPanelProps {
  ruleId: string;
}

const ScopeBadge: React.FC<{ scope: 'global' | 'scoped' | 'unassigned' }> = ({ scope }) => {
  if (scope === 'global') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
        <Globe className="w-3 h-3" /> Global
      </span>
    );
  }
  if (scope === 'unassigned') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        <CircleOff className="w-3 h-3" /> Unassigned (inert)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
      <Target className="w-3 h-3" /> Scoped
    </span>
  );
};

export const AssignmentPanel: React.FC<AssignmentPanelProps> = ({ ruleId }) => {
  const { data: assignments, isLoading } = useRuleAssignments(ruleId);
  const addMutation = useAddRuleAssignment();
  const removeMutation = useRemoveRuleAssignment();

  const [pickerKind, setPickerKind] = useState<'capacity' | 'lakehouse'>('capacity');
  const [pickerValue, setPickerValue] = useState('');

  const { data: capacities } = useQuery({
    queryKey: ['alert-rules', 'assignment-picker', 'capacities'],
    queryFn: () => getCapacities(),
    staleTime: 60000,
  });
  const { data: lakehouses } = useLakehouses();

  const capacityOptions = useMemo(() => {
    const assigned = new Set((assignments?.capacities ?? []).map((a) => a.capacityId));
    return (capacities ?? [])
      .filter((c) => !assigned.has(c.id))
      .map((c) => ({ value: c.id, label: c.capacity_name }));
  }, [capacities, assignments]);

  const lakehouseOptions = useMemo(() => {
    const assigned = new Set((assignments?.lakehouses ?? []).map((a) => a.lakehouseId));
    return (lakehouses?.data ?? [])
      .filter((lh: LakehouseWithWorkspace) => !assigned.has(lh.id))
      .map((lh: LakehouseWithWorkspace) => ({ value: lh.id, label: lh.lakehouseName ?? lh.id }));
  }, [lakehouses, assignments]);

  const options = pickerKind === 'capacity' ? capacityOptions : lakehouseOptions;

  const handleAdd = () => {
    if (!pickerValue) return;
    addMutation.mutate(
      {
        id: ruleId,
        target: pickerKind === 'capacity' ? { capacityId: pickerValue } : { lakehouseId: pickerValue },
      },
      { onSuccess: () => setPickerValue('') }
    );
  };

  if (isLoading || !assignments) {
    return <div className="text-sm text-gray-400">Loading targeting…</div>;
  }

  return (
    <div className="space-y-3" data-testid="assignment-panel">
      <div className="flex items-center gap-2">
        <ScopeBadge scope={assignments.scope} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {assignments.capacities.length + assignments.lakehouses.length} assignment(s)
        </span>
      </div>

      {/* Current assignments */}
      {(assignments.capacities.length > 0 || assignments.lakehouses.length > 0) && (
        <ul className="space-y-1">
          {assignments.capacities.map((a) => (
            <li key={`cap-${a.capacityId}`}
                className="flex items-center justify-between rounded bg-gray-50 dark:bg-gray-700/50 px-2 py-1 text-sm">
              <span>
                <span className="text-xs uppercase text-gray-400 mr-2">Capacity</span>
                {a.capacityName ?? a.capacityId}
              </span>
              <button
                type="button"
                aria-label={`Remove capacity ${a.capacityName ?? a.capacityId}`}
                className="text-gray-400 hover:text-red-500"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate({ id: ruleId, target: { capacityId: a.capacityId } })}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
          {assignments.lakehouses.map((a) => (
            <li key={`lh-${a.lakehouseId}`}
                className="flex items-center justify-between rounded bg-gray-50 dark:bg-gray-700/50 px-2 py-1 text-sm">
              <span>
                <span className="text-xs uppercase text-gray-400 mr-2">Lakehouse</span>
                {a.lakehouseName ?? a.lakehouseId}
              </span>
              <button
                type="button"
                aria-label={`Remove lakehouse ${a.lakehouseName ?? a.lakehouseId}`}
                className="text-gray-400 hover:text-red-500"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate({ id: ruleId, target: { lakehouseId: a.lakehouseId } })}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add picker */}
      <div className="flex items-center gap-2">
        <select
          className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm py-1"
          value={pickerKind}
          onChange={(e) => { setPickerKind(e.target.value as 'capacity' | 'lakehouse'); setPickerValue(''); }}
        >
          <option value="capacity">Capacity</option>
          <option value="lakehouse">Lakehouse</option>
        </select>
        <select
          className="flex-1 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm py-1"
          value={pickerValue}
          onChange={(e) => setPickerValue(e.target.value)}
        >
          <option value="">Select {pickerKind}…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm px-2 py-1 disabled:opacity-50"
          disabled={!pickerValue || addMutation.isPending}
          onClick={handleAdd}
        >
          <Plus className="w-4 h-4" /> Assign
        </button>
      </div>
    </div>
  );
};

export default AssignmentPanel;
